import type {
  Actievoorstel,
  AnalyticsResponse,
  AppSettings,
  AppSettingsUpdate,
  BatchApproveResponse,
  BulkRecategorizeRequest,
  BulkRecategorizeResponse,
  Categorisation,
  CategorisationDetail,
  CorrectionResponse,
  ListResponse,
  MetricsResponse,
  ProcessingAttempt,
  RecategorizeRequest,
  SamenvattingType,
  SkillStatus,
  SlackTestResponse,
  RelocationJob,
  VaultWriteFailure,
  VaultWriteFailureStats,
  VoorstellenLijst,
  VoorstelWijziging,
  WebhookEvent,
  WebhookEventStats,
  WebhookRetryResponse,
} from '../types'
import { supabase } from './supabase'
import { unwrapList } from './unwrapList'

const API_URL = import.meta.env.VITE_API_URL || ''

async function authedFetch(url: string, init: RequestInit): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  return fetch(`${API_URL}${url}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
      ...init.headers,
    },
  })
}

export async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  let res = await authedFetch(url, init)

  if (res.status === 401) {
    const { data, error } = await supabase.auth.refreshSession()
    if (!error && data.session) {
      res = await authedFetch(url, init) // retry once with the refreshed token
    }
    if (res.status === 401) {
      // Hard 401: drop the session so AuthProvider redirects to /login.
      await supabase.auth.signOut()
    }
  }

  return res
}

/**
 * Extract a meaningful error message from a failed API response.
 */
export async function getErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    return body.detail || body.error || `${fallback}: ${res.status}`
  } catch {
    return `${fallback}: ${res.status}`
  }
}

/** De server meldt 409: iemand anders heeft intussen over dit gesprek besloten. */
export class ConflictError extends Error {}

async function jsonOfFout<T>(res: Response, fallback: string): Promise<T> {
  if (res.status === 409) throw new ConflictError(await getErrorMessage(res, fallback))
  if (!res.ok) throw new Error(await getErrorMessage(res, fallback))
  return res.json()
}

export const api = {
  /**
   * Get list of categorisations with optional filters.
   */
  getCategorisations: async (
    params?: Record<string, string>
  ): Promise<ListResponse<Categorisation>> => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    const res = await fetchWithAuth(`/api/categorisations${query}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch categorisations'))
    return res.json()
  },

  /**
   * Get items needing review (below confidence threshold and needs_review=true).
   */
  getReviewQueue: async (): Promise<ListResponse<Categorisation>> => {
    const res = await fetchWithAuth('/api/categorisations/review-queue')
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch review queue'))
    return res.json()
  },

  /**
   * Approve a categorisation as correct, removing it from the review queue.
   */
  approveCategorisation: async (id: string): Promise<Categorisation> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}/approve`, {
      method: 'POST',
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to approve categorisation'))
    return res.json()
  },

  /**
   * Approve multiple categorisations at once, removing them from the review queue.
   */
  batchApproveCategorisations: async (ids: string[]): Promise<BatchApproveResponse> => {
    const res = await fetchWithAuth('/api/categorisations/batch-approve', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to batch approve'))
    return res.json()
  },

  /**
   * Get a single categorisation by ID, including the wachtrij-dossier fields (G-06).
   */
  getCategorisation: async (id: string): Promise<CategorisationDetail> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch categorisation'))
    return res.json()
  },

  /**
   * Save edits to a wachtend meeting's summary (G-06). Throws ConflictError (409) when the
   * meeting is no longer wachtend or has no summary.
   */
  saveSamenvatting: async (
    id: string,
    samenvatting_type: SamenvattingType | null,
    velden: Record<string, unknown>,
  ): Promise<CategorisationDetail> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}/samenvatting`, {
      method: 'PUT',
      body: JSON.stringify({ samenvatting_type, velden }),
    })
    return jsonOfFout(res, 'Opslaan van de samenvatting mislukt')
  },

  /**
   * Discard edits and revert to the LLM-generated summary (G-06). Throws ConflictError (409)
   * under the same conditions as saveSamenvatting.
   */
  resetSamenvatting: async (id: string): Promise<CategorisationDetail> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}/samenvatting`, {
      method: 'DELETE',
    })
    return jsonOfFout(res, 'Terugzetten van de samenvatting mislukt')
  },

  /**
   * Make only the summary again, in the type of the current category (A-20). Does NOT
   * re-categorise, unlike reprocess. Throws ConflictError (409) when the meeting is no longer
   * wachtend or its category changed meanwhile.
   */
  samenvattingOpnieuw: async (id: string): Promise<CategorisationDetail> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}/samenvatting/opnieuw`, {
      method: 'POST',
    })
    return jsonOfFout(res, 'Opnieuw maken van de samenvatting mislukt')
  },

  /** Actievoorstellen van één gesprek (G-07). */
  getVoorstellen: async (categorisationId: string): Promise<VoorstellenLijst> => {
    const res = await fetchWithAuth(`/api/categorisations/${categorisationId}/voorstellen`)
    return jsonOfFout(res, 'Voorstellen laden mislukt')
  },

  /** Wijzig één voorstel vóór bevestigen. 409 (ConflictError) als er al over het gesprek besloten is. */
  updateVoorstel: async (
    categorisationId: string,
    voorstelId: string,
    wijziging: VoorstelWijziging,
  ): Promise<Actievoorstel> => {
    const res = await fetchWithAuth(
      `/api/categorisations/${categorisationId}/voorstellen/${encodeURIComponent(voorstelId)}`,
      { method: 'PATCH', body: JSON.stringify(wijziging) },
    )
    return jsonOfFout(res, 'Opslaan van het voorstel mislukt')
  },

  /** Leden, labels en de laatste run van de skill (G-07). */
  getSkillStatus: async (): Promise<SkillStatus> => {
    const res = await fetchWithAuth('/api/skill-status')
    return jsonOfFout(res, 'Status van de skill laden mislukt')
  },

  /**
   * Get correction history for a categorisation.
   */
  getCorrections: async (categorisationId: string): Promise<CorrectionResponse[]> => {
    const res = await fetchWithAuth(`/api/categorisations/${categorisationId}/corrections`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch corrections'))
    return unwrapList<CorrectionResponse[]>(await res.json())
  },

  /**
   * Get operational metrics for a time period.
   */
  getMetrics: async (periodHours: number = 24): Promise<MetricsResponse> => {
    const res = await fetchWithAuth(`/api/metrics?period_hours=${periodHours}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch metrics'))
    return res.json()
  },

  /**
   * Get analytics data for charts and statistics.
   */
  getAnalytics: async (periodDays: number = 30): Promise<AnalyticsResponse> => {
    const res = await fetchWithAuth(`/api/analytics?period_days=${periodDays}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch analytics'))
    return res.json()
  },

  /**
   * Get current application settings.
   */
  getSettings: async (): Promise<AppSettings> => {
    const res = await fetchWithAuth('/api/settings')
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch settings'))
    return res.json()
  },

  /**
   * Update application settings (partial update).
   */
  updateSettings: async (data: AppSettingsUpdate): Promise<AppSettings> => {
    const res = await fetchWithAuth('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to update settings'))
    return res.json()
  },

  testSlackAlert: async (): Promise<SlackTestResponse> => {
    const res = await fetchWithAuth('/api/settings/test-slack', {
      method: 'POST',
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to send test Slack alert'))
    return res.json()
  },

  /**
   * Download categorisations as CSV. Accepts the same filter params as the list endpoint.
   * Triggers a browser file download via blob URL.
   */
  exportCsv: async (params?: Record<string, string>): Promise<void> => {
    const filtered = params
      ? Object.fromEntries(Object.entries(params).filter(([, v]) => v))
      : {}
    const query = Object.keys(filtered).length > 0 ? `?${new URLSearchParams(filtered)}` : ''
    const res = await fetchWithAuth(`/api/categorisations/export${query}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to export CSV'))

    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const filename =
      res.headers.get('Content-Disposition')?.match(/filename="?(.+?)"?$/)?.[1]
      ?? 'categorisations.csv'

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  },

  // ===========================================================================
  // Recategorize / Reprocess
  // ===========================================================================

  recategorize: async (id: string, data: RecategorizeRequest): Promise<Categorisation> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}/recategorize`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to recategorize'))
    return res.json()
  },

  reprocess: async (id: string): Promise<WebhookEvent> => {
    const res = await fetchWithAuth(`/api/categorisations/${id}/reprocess`, {
      method: 'POST',
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to reprocess'))
    return res.json()
  },

  bulkRecategorize: async (data: BulkRecategorizeRequest): Promise<BulkRecategorizeResponse> => {
    const res = await fetchWithAuth('/api/categorisations/bulk-recategorize', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to bulk recategorize'))
    return res.json()
  },

  // ===========================================================================
  // Webhook Events
  // ===========================================================================

  getWebhookEventStats: async (): Promise<WebhookEventStats> => {
    const res = await fetchWithAuth('/api/admin/webhook-events/stats')
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch webhook stats'))
    return res.json()
  },

  getWebhookEvents: async (
    params?: Record<string, string>
  ): Promise<ListResponse<WebhookEvent>> => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    const res = await fetchWithAuth(`/api/admin/webhook-events${query}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch webhook events'))
    return res.json()
  },

  getProcessingAttempts: async (meetingId: string): Promise<ProcessingAttempt[]> => {
    const res = await fetchWithAuth(`/api/admin/webhook-events/${meetingId}/attempts`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch processing attempts'))
    return unwrapList<ProcessingAttempt[]>(await res.json())
  },

  retryWebhookEvent: async (id: string): Promise<WebhookEvent> => {
    const res = await fetchWithAuth(`/api/admin/webhook-events/${id}/retry`, {
      method: 'POST',
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to retry webhook event'))
    return res.json()
  },

  retryAllFailed: async (): Promise<WebhookRetryResponse> => {
    const res = await fetchWithAuth('/api/admin/webhook-events/retry-failed', {
      method: 'POST',
    })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to retry failed events'))
    return res.json()
  },

  // ===========================================================================
  // Vault Write Failures
  // ===========================================================================

  getVaultWriteFailures: async (
    params?: Record<string, string>,
  ): Promise<ListResponse<VaultWriteFailure>> => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    const res = await fetchWithAuth(`/api/admin/vault-failures${query}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch vault failures'))
    return res.json()
  },

  getVaultWriteFailureStats: async (): Promise<VaultWriteFailureStats> => {
    const res = await fetchWithAuth('/api/admin/vault-failures/stats')
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch vault failure stats'))
    return res.json()
  },

  retryVaultWriteFailure: async (id: string): Promise<VaultWriteFailure> => {
    const res = await fetchWithAuth(`/api/admin/vault-failures/${id}/retry`, { method: 'POST' })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to retry vault write'))
    return res.json()
  },

  retryAllVaultWriteFailures: async (): Promise<{ attempted: number; resolved: number }> => {
    const res = await fetchWithAuth('/api/admin/vault-failures/retry-all', { method: 'POST' })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to retry all vault writes'))
    return res.json()
  },

  // ===========================================================================
  // Relocation Jobs
  // ===========================================================================

  getRelocationJobs: async (
    params?: Record<string, string>,
  ): Promise<ListResponse<RelocationJob>> => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    const res = await fetchWithAuth(`/api/admin/relocation-jobs${query}`)
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch relocation jobs'))
    return res.json()
  },

  retryRelocationJob: async (id: string): Promise<RelocationJob> => {
    const res = await fetchWithAuth(`/api/admin/relocation-jobs/${id}/retry`, { method: 'POST' })
    if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to retry relocation'))
    return res.json()
  },
}
